import React, { useState } from 'react';
import {
  ChatBubbleLeftIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const ConversationViewer = ({ call }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Parse transcript into conversation turns
  const parseTranscript = (transcript) => {
    if (!transcript) return [];

    const lines = transcript.split('\n').filter(line => line.trim());
    const conversation = [];

    for (const line of lines) {
      // Match pattern: [timestamp] Speaker: message
      const match = line.match(/\[([^\]]+)\]\s*(Customer|AI|Agent|System):\s*(.+)/i);

      if (match) {
        const [, timestamp, speaker, message] = match;
        conversation.push({
          timestamp,
          speaker: speaker.toLowerCase(),
          message: message.trim()
        });
      } else if (line.includes(':')) {
        // Fallback for simpler format: Speaker: message
        const [speaker, ...messageParts] = line.split(':');
        const message = messageParts.join(':').trim();

        if (message) {
          conversation.push({
            timestamp: null,
            speaker: speaker.trim().toLowerCase(),
            message
          });
        }
      }
    }

    return conversation;
  };

  const conversation = parseTranscript(call.transcript);

  const getSpeakerInfo = (speaker) => {
    switch (speaker) {
      case 'customer':
        return {
          name: call.contactName || 'Customer',
          color: 'bg-blue-500',
          icon: UserIcon,
          align: 'left',
          bgColor: 'bg-blue-50'
        };
      case 'ai':
      case 'agent':
      case 'system':
        return {
          name: 'AI Assistant',
          color: 'bg-green-500',
          icon: ChatBubbleLeftIcon,
          align: 'right',
          bgColor: 'bg-green-50'
        };
      default:
        return {
          name: speaker,
          color: 'bg-gray-500',
          icon: ChatBubbleLeftIcon,
          align: 'left',
          bgColor: 'bg-gray-50'
        };
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      // If it's already a time string, return it
      if (timestamp.includes(':')) return timestamp;

      // Try to parse as date
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  if (!call.transcript || conversation.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <ChatBubbleLeftIcon className="h-10 w-10 mx-auto mb-2 text-gray-400" />
        <p>No conversation recorded</p>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 mt-4 pt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center space-x-2">
          <ChatBubbleLeftIcon className="h-5 w-5 text-gray-600" />
          <span className="font-medium text-gray-900">Conversation</span>
          <span className="text-sm text-gray-500">({conversation.length} messages)</span>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
          {conversation.map((turn, index) => {
            const speakerInfo = getSpeakerInfo(turn.speaker);
            const Icon = speakerInfo.icon;

            return (
              <div
                key={index}
                className={`flex ${speakerInfo.align === 'right' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-2xl ${speakerInfo.align === 'right' ? 'items-end' : 'items-start'} flex flex-col`}>
                  {/* Speaker name and timestamp */}
                  <div className="flex items-center space-x-2 mb-1 px-3">
                    <div className={`w-6 h-6 rounded-full ${speakerInfo.color} flex items-center justify-center`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-xs font-medium text-gray-700">{speakerInfo.name}</span>
                    {turn.timestamp && (
                      <>
                        <span className="text-xs text-gray-400">•</span>
                        <div className="flex items-center text-xs text-gray-500">
                          <ClockIcon className="h-3 w-3 mr-1" />
                          {formatTimestamp(turn.timestamp)}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Message bubble */}
                  <div className={`${speakerInfo.bgColor} rounded-lg px-4 py-3 shadow-sm`}>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{turn.message}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary footer */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div>
              <span className="text-gray-500">Duration:</span>{' '}
              <span className="font-medium text-gray-900">
                {call.duration ? `${Math.floor(call.duration / 60)}m ${call.duration % 60}s` : '0s'}
              </span>
            </div>
            {call.outcome && (
              <div>
                <span className="text-gray-500">Outcome:</span>{' '}
                <span className="font-medium text-gray-900 capitalize">
                  {call.outcome.replace(/_/g, ' ')}
                </span>
              </div>
            )}
          </div>
          {call.cost && (
            <div>
              <span className="text-gray-500">Cost:</span>{' '}
              <span className="font-medium text-gray-900">${(Number(call.cost) || 0).toFixed(4)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConversationViewer;
